import { useWorkspaceFileRecords } from "@/api/fileRecords";
import { useWorkspaceFlowSessions } from "@/api/flow_sessions";
import { useChatFolders, useFolders } from "@/api/folders";
import { useWorkspaceCanvases } from "@/api/workspaces";
import type {
  Canvas,
  FileRecord,
  Folder,
  InboxItem,
} from "@cohesive/api-client";
import { useMemo } from "react";
import type { HomeTab } from "../utils/homeBrowserTypes";

export interface WorkspaceBrowserData {
  /** Full workspace sets — unscoped by folder; the browser scopes per level. */
  canvases: Canvas[];
  files: FileRecord[];
  chats: InboxItem[];
  canvasFolders: Folder[];
  chatFolders: Folder[];
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Data for the workspace browser's typed tabs (Files / Canvases / Chats):
 * the workspace's FULL resource sets including folders, uncapped — unlike
 * `useHomeBrowserData`, which feeds the recents-style Recent/Starred tabs.
 * Only the active tab's queries are enabled; caches persist across tabs.
 */
export function useWorkspaceBrowserData(
  tab: HomeTab,
  workspaceId: string,
): WorkspaceBrowserData {
  const isFiles = tab === "recents";
  const isCanvases = tab === "canvases";
  const isChats = tab === "chats";

  const canvasesQuery = useWorkspaceCanvases(workspaceId, {
    enabled: isCanvases,
  });
  // Files load on every tab: the file set also feeds the bulk-move dialog,
  // folder cascade-delete, and unique-name generation page-wide.
  const filesQuery = useWorkspaceFileRecords(workspaceId);
  const chatsQuery = useWorkspaceFlowSessions(workspaceId, {
    enabled: isChats,
  });
  const canvasFoldersQuery = useFolders(workspaceId, { enabled: isCanvases });
  const chatFoldersQuery = useChatFolders(workspaceId, { enabled: isChats });

  // Client-side safety filter mirroring the old detail page: only chats that
  // actually belong to this workspace (guards against stale cache entries).
  const chats = useMemo(
    () =>
      (chatsQuery.data ?? []).filter(
        (item) => item.flow_session?.workspace_id === workspaceId,
      ),
    [chatsQuery.data, workspaceId],
  );

  const activeQueries = isFiles
    ? [filesQuery]
    : isCanvases
      ? [canvasesQuery, canvasFoldersQuery]
      : isChats
        ? [chatsQuery, chatFoldersQuery]
        : [];

  return {
    canvases: canvasesQuery.data ?? [],
    files: filesQuery.data ?? [],
    chats,
    canvasFolders: canvasFoldersQuery.data ?? [],
    chatFolders: chatFoldersQuery.data ?? [],
    isPending:
      activeQueries.length > 0 && activeQueries.every((q) => q.isPending),
    isError: activeQueries.some((q) => q.isError),
    refetch: () => activeQueries.forEach((q) => void q.refetch()),
  };
}
